/**
 * Web mirror of the server's ONE low-stock predicate
 * (backend/src/modules/inventory/low-stock.ts — `isLowStock` /
 * `computeNeedsPurchase`). Keep the two in lockstep: the inventory list
 * filter, the stat cards, the row caption and the offline replica reader must
 * all agree with what GET /inventory/low-stock and GET /inventory/stats say.
 *
 * Rule: on-hand `<= 0` is ALWAYS low stock; thresholds only raise the bar.
 * Thresholds are inclusive and an explicit `0` threshold counts (`!= null`,
 * never truthiness — the old `item.lowStockAlert && …` pattern silently
 * ignored a 0 threshold).
 *
 * Two web-only tolerances the server doesn't need:
 *  - `needsPurchase === true` (the server-persisted verdict) also counts, so a
 *    row the server already flagged shows as low even if the local row is
 *    missing its thresholds. A persisted `false` does NOT override a computed
 *    low (legacy zero-quantity rows written before the server recomputed the
 *    flag must still surface — that was the reported bug).
 *  - a non-numeric `quantity` (an offline replica row that never synced a
 *    number) is never low stock rather than throwing or coercing.
 */
export interface LowStockInput {
  quantity: number;
  lowStockAlert?: number | null;
  reorderPoint?: number | null;
  needsPurchase?: boolean | null;
}

const asFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * The configured bar an item is judged against — the higher of its
 * `lowStockAlert` and per-location `reorderPoint` — or `null` when neither is
 * set (no fabricated default; mirrors the server's `lowStockThreshold`). Used
 * by the offline `/reports/inventory` mirror for the Warning band + minRequired.
 */
export function lowStockThresholdOf(item: Pick<LowStockInput, 'lowStockAlert' | 'reorderPoint'>): number | null {
  const bars = [asFiniteNumber(item.lowStockAlert), asFiniteNumber(item.reorderPoint)].filter(
    (v): v is number => v !== null,
  );
  return bars.length ? Math.max(...bars) : null;
}

export function isLowStockItem(item: LowStockInput): boolean {
  const quantity = asFiniteNumber(item.quantity);
  if (quantity === null) return false;
  if (item.needsPurchase === true) return true;
  if (quantity <= 0) return true;
  const lowStockAlert = asFiniteNumber(item.lowStockAlert);
  if (lowStockAlert !== null && quantity <= lowStockAlert) return true;
  const reorderPoint = asFiniteNumber(item.reorderPoint);
  if (reorderPoint !== null && quantity <= reorderPoint) return true;
  return false;
}
